import type { ProfileParams, UserLocation } from '@/api/schema'
import { Row } from '@/app/components/Flex/Flex'
import { Input } from '@/app/components/Input/Input'
import { Layout } from '@/app/components/Layout/Layout'
import { LocationFormPage } from '@/app/components/LocationFormPage/LocationFormPage'
import { Motion } from '@/app/components/Motion/Motion'
import { Terms } from '@/app/components/Terms/Terms'
import { Lib } from '@/app/components/UI/Lib'
import { UI } from '@/app/components/UI/UI'
import { OnboardingType } from '@/app/const'
import { fromISOTime, toISOTime } from '@/app/utils/date'
import { parseError } from '@/app/utils/error'
import { metrikaGoal } from '@/app/utils/metrika'
import type { FunctionalComponent } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { IMask, IMaskInput } from 'react-imask'

enum View {
  Form = 'form',
  Search = 'search',
}

export const LocationView: FunctionalComponent<{
  type: OnboardingType
  location?: UserLocation | null
  birthTime?: string | null
  patchProfile: (params: ProfileParams) => Promise<void> | void
  onBack: () => void
}> = props => {
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const [view, setView] = useState(View.Form)

  const [location, setLocation] = useState<UserLocation | undefined>(
    props.location ?? undefined,
  )

  const [birthTime, setBirthTime] = useState(
    props?.birthTime ? fromISOTime(props.birthTime) : '',
  )

  const timeValid = birthTime === '' || /^\d{2}:\d{2}$/.test(birthTime)

  useEffect(() => {
    metrikaGoal('onboard_location')
  }, [])

  if (view === View.Search) {
    return (
      <Layout.Content
        help
        key={`location-view-${props.type}`}
        onBack={() => {
          setView(View.Form)
        }}
      >
        <Motion>
          <Row gap={15}>
            <UI.Avatar
              label
              size={64}
              bot={props.type}
            />

            <LocationFormPage
              onSelect={async location => {
                setLocation(location)
                setView(View.Form)
              }}
            />

            <UI.Button
              type='secondary'
              onClick={() => {
                setView(View.Form)
              }}
            >
              Назад
            </UI.Button>
          </Row>
        </Motion>
      </Layout.Content>
    )
  }

  return (
    <Layout.Content
      help
      onBack={props.onBack}
      key={`location-view-${props.type}`}
    >
      <form
        onSubmit={async e => {
          e.preventDefault()
          try {
            setLoading(true)
            await props.patchProfile({
              location: location ?? undefined,
              birthTime: toISOTime(birthTime),
            })
            metrikaGoal('login_submit_bday')
          }
          catch (e) {
            console.error(e)
            setErrors(parseError(e))
          }
          finally {
            setLoading(false)
          }
        }}
      >
        <Motion>
          <Row gap={15}>
            <UI.Avatar
              label
              size={64}
              bot={props.type}
            />

            <Lib.Fields>
              <Lib.SystemMessage>
                Чтобы натальная карта была точнее, добавь время и место рождения.
              </Lib.SystemMessage>

              <Input.Block label='Место рождения (необязательно)'>
                <Input
                  showArrow
                  disabled={loading}
                  value={location?.name_ru}
                  placeholder='Выбери город'
                  onClick={() => {
                    setView(View.Search)
                  }}
                  onFocus={() => {
                    setView(View.Search)
                  }}
                />
              </Input.Block>

              <Input.Block label='Время рождения (необязательно)'>
                <Input
                  type='tel'
                  placeholder='HH:MM'
                  value={birthTime}
                  onInput={setBirthTime}
                  onChange={setBirthTime}
                  disabled={loading}
                >
                  {props => (
                    <IMaskInput
                      {...props}
                      overwrite
                      type='tel'
                      mask={Date}
                      pattern='H:M'
                      blocks={{
                        H: {
                          mask: IMask.MaskedRange,
                          from: 0,
                          to: 23,
                          maxLength: 2,
                        },
                        M: {
                          mask: IMask.MaskedRange,
                          from: 0,
                          to: 59,
                          maxLength: 2,
                        },
                      }}
                      format={(date: Date | null) => {
                        if (!date) return ''
                        const hours = String(date.getHours()).padStart(2, '0')
                        const minutes = String(date.getMinutes()).padStart(2, '0')
                        return `${hours}:${minutes}`
                      }}
                      parse={(value: string) => {
                        if (!value) return null
                        const [hours, minutes] = value.split(':').map(Number)
                        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
                        const date = new Date()
                        date.setHours(hours, minutes, 0, 0)
                        return date
                      }}
                    />
                  )}
                </Input>
              </Input.Block>
            </Lib.Fields>

            <Lib.Errors errors={errors} />

            <Row gap={15}>
              <UI.Button
                size='l'
                type='primary'
                htmlType='submit'
                disabled={loading || !timeValid}
              >
                Продолжить
              </UI.Button>

              <UI.Hint>
                <Terms />
              </UI.Hint>
            </Row>
          </Row>
        </Motion>
      </form>
    </Layout.Content>
  )
}
